import { IEnergyLevelApiResponse, IDynamicWeights } from '@vacationtracker/shared/types/user-insight'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { EnergyLevelData } from '../types'
import { getEnergyModeConfig } from './energy-mode-config'
import { FormatMessageFunction } from './api'

export const transformEnergyScoreResponse = (response: IEnergyLevelApiResponse, formatMessage?: FormatMessageFunction): EnergyLevelData => {
  const score = response.score
  const energyMode = getEnergyMode(score)
  const modeConfig = formatMessage
    ? getEnergyModeConfig(score, formatMessage)
    : {
        topBannerMessage: `Energy score: ${score}`,
        humanCenteredMessage: `Your current energy level is ${score}`,
      }
  const meta = response.latestEnergyScore?.meta

  // Component mapping with display names
  const componentConfig = {
    recentRest: { name: 'Recent Rest' },
    ptoBalance: { name: 'PTO Balance' },
    leavePattern: { name: 'Leave Pattern' },
    planning: { name: 'Planning' },
    rolloverHealth: { name: 'Rollover Health' },
    accrualHealth: { name: 'Accrual Health' },
    workLifeBalance: { name: 'Work-Life Balance' },
  }

  // Dynamically generate score breakdown from available components with embedded contribution data
  const rawScoreBreakdown = meta?.components
    ? Object.entries(meta.components)
        .filter(([key, component]) => component && typeof component.score === 'number')
        .map(([key, component]) => {
          const config = componentConfig[key as keyof typeof componentConfig]

          // Use embedded contribution data if available, otherwise fall back to old weights format
          if ((component as any).contribution) {
            const contribution = (component as any).contribution
            return {
              name: config.name,
              weight: Math.round(contribution.current * 100), // Convert decimal to points (0.25 -> 25)
              maxWeight: Math.round(contribution.max * 100), // Convert decimal to points (0.35 -> 35)
              score: Number((component as any).score),
              color: getScoreColor((component as any).score),
            }
          } else {
            // Fallback to old weights format for backward compatibility
            const weight = meta?.weights?.[key as keyof IDynamicWeights] || 0
            return {
              name: config.name,
              weight: Math.round(weight * 100), // Convert from decimal to percentage
              maxWeight: Math.round(weight * 100), // Use weight as maxWeight for backward compatibility
              score: Number((component as any).score),
              color: getScoreColor((component as any).score),
            }
          }
        })
    : []

  // Combine accrual health and rollover health into a single rollover health item
  const scoreBreakdown = (() => {
    const accrualHealth = rawScoreBreakdown.find((factor) => factor.name === 'Accrual Health')
    const rolloverHealth = rawScoreBreakdown.find((factor) => factor.name === 'Rollover Health')

    if (accrualHealth && rolloverHealth) {
      // Create combined rollover health item
      const combinedItem = {
        name: 'Rollover Health',
        weight: accrualHealth.weight + rolloverHealth.weight,
        maxWeight: (accrualHealth.maxWeight || 0) + (rolloverHealth.maxWeight || 0),
        score: Math.round((accrualHealth.score + rolloverHealth.score) / 2), // Average score
        color: getScoreColor(Math.round((+accrualHealth.score + +rolloverHealth.score) / 2)),
      }

      // Return breakdown with combined item, excluding original accrual and rollover items
      return rawScoreBreakdown
        .filter((factor) => factor.name !== 'Accrual Health' && factor.name !== 'Rollover Health')
        .concat(combinedItem)
        .sort((a, b) => (b.maxWeight || 0) - (a.maxWeight || 0))
    }

    return rawScoreBreakdown.sort((a, b) => (b.maxWeight || 0) - (a.maxWeight || 0))
  })()

  const suggestedActions =
    meta?.recommendations?.map((rec, index) => ({
      id: `action-${index}`,
      title: rec.action,
      priority: rec.priority as 'high' | 'medium' | 'low',
      energyPoints: calculateEnergyPoints(rec.priority),
      benefits: [rec.impact],
      description: rec.timeframe,
    })) || []

  // Create energy trend from user history
  const energyTrend =
    response.history && response.history.length > 0
      ? response.history.map((entry, index, array) => {
          const date = new Date(entry.timestamp)

          // Check if there are multiple entries on the same day
          const sameDay = array.filter((item) => new Date(item.timestamp).toDateString() === date.toDateString())

          let dateLabel: string
          if (sameDay.length === 1) {
            // Single entry per day - just date
            dateLabel = date.toISOString().split('T')[0] // YYYY-MM-DD
          } else {
            // Multiple entries per day - check if we need seconds for uniqueness
            const sameMinute = array.filter((item) => {
              const otherDate = new Date(item.timestamp)
              return otherDate.toISOString().substring(0, 16) === date.toISOString().substring(0, 16)
            })

            if (sameMinute.length > 1) {
              // Multiple entries in same minute - include seconds
              dateLabel = date.toISOString().substring(0, 19) // YYYY-MM-DDTHH:MM:SS
            } else {
              // Multiple entries same day but different minutes
              dateLabel = date.toISOString().substring(0, 16) // YYYY-MM-DDTHH:MM
            }
          }

          return {
            date: dateLabel,
            score: entry.score,
          }
        })
      : []

  // Detect algorithm version changes in history
  const algorithmUpdateDates: string[] =
    response.history && response.history.length > 1
      ? response.history.reduce<string[]>((dates, entry, index, array) => {
          if (index > 0 && entry.algorithmVersion && array[index - 1].algorithmVersion && entry.algorithmVersion !== array[index - 1].algorithmVersion) {
            // Use the same date label that energyTrend uses for this entry
            const trendEntry = energyTrend[index]
            if (trendEntry) {
              dates.push(trendEntry.date)
            }
          }
          return dates
        }, [])
      : []

  // Create department trend from department history
  const departmentTrend =
    response.departmentHistory && response.departmentHistory.length > 0
      ? response.departmentHistory.map((entry, index, array) => {
          const date = new Date(entry.timestamp)

          // Check if there are multiple entries on the same day
          const sameDay = array.filter((item) => new Date(item.timestamp).toDateString() === date.toDateString())

          let dateLabel: string
          if (sameDay.length === 1) {
            // Single entry per day - just date
            dateLabel = date.toISOString().split('T')[0] // YYYY-MM-DD
          } else {
            // Multiple entries per day - check if we need seconds for uniqueness
            const sameMinute = array.filter((item) => {
              const otherDate = new Date(item.timestamp)
              return otherDate.toISOString().substring(0, 16) === date.toISOString().substring(0, 16)
            })

            if (sameMinute.length > 1) {
              // Multiple entries in same minute - include seconds
              dateLabel = date.toISOString().substring(0, 19) // YYYY-MM-DDTHH:MM:SS
            } else {
              // Multiple entries same day but different minutes
              dateLabel = date.toISOString().substring(0, 16) // YYYY-MM-DDTHH:MM
            }
          }

          return {
            date: dateLabel,
            score: entry.score,
          }
        })
      : []

  // Calculate trend from history
  const trend = response.history && response.history.length > 1 ? response.history[response.history.length - 1].score - response.history[response.history.length - 2].score : 0

  // Create trend alert from actual data
  const trendAlert =
    response.history && response.history.length > 1 && trend !== 0
      ? `Trend Alert: Your energy score has ${trend > 0 ? 'increased' : 'declined'} ${Math.abs(trend)} points recently.`
      : ''

  // Build feedbackInfo using the feedbackModifier
  const feedbackInfo = meta?.feedbackModifier
    ? {
        ...meta.feedbackModifier,
        allFeedbacks: response.feedbackHistory || [],
      }
    : undefined

  return {
    currentScore: {
      current: score,
      trend,
    },
    energyMode,
    modeDescription: '',
    daysSinceLastBreak: meta?.components?.recentRest?.daysSinceLastBreak || 0,
    suggestedActions,
    dateOptions: [], // This would come from a separate API
    energyTrend,
    departmentTrend,
    scoreBreakdown,
    algorithmUpdateDates,
    trendAlert,
    actionEffect: 'Action Effect: Reverse the trend and get 18 points by taking the recommended action.',
    alertMessage: modeConfig.humanCenteredMessage,
    topBannerMessage: modeConfig.topBannerMessage,
    humanCenteredMessage: modeConfig.humanCenteredMessage,
    meta,
    feedbackInfo,
  }
}

const getScoreColor = (score: number): 'red' | 'orange' | 'blue' | 'green' => {
  if (score >= 80) return 'green'
  if (score >= 60) return 'blue'
  if (score >= 40) return 'orange'
  return 'red'
}

const calculateEnergyPoints = (priority: string): number => {
  switch (priority) {
    case 'high':
      return 25
    case 'medium':
      return 15
    case 'low':
      return 10
    default:
      return 10
  }
}
